export const INCOMEIQUEUE_RCM_NETWORK_TYPES = {
  IN_NETWORK: 'in_network',
  OUT_OF_NETWORK: 'out_of_network',
  SELF_PAY: 'self_pay'
};

export const INCOMEIQUEUE_RCM_NETWORK_TABS = [
  { value: INCOMEIQUEUE_RCM_NETWORK_TYPES.IN_NETWORK, label: 'In-network' },
  { value: INCOMEIQUEUE_RCM_NETWORK_TYPES.OUT_OF_NETWORK, label: 'Out-of-network' },
  { value: INCOMEIQUEUE_RCM_NETWORK_TYPES.SELF_PAY, label: 'Self pay' }
];

export const INCOMEIQUEUE_RCM_SCENARIO_FIELDS = {
  OVER_PAYMENT: 'over_payment',
  UNDER_PAYMENT: 'under_payment'
};

export const INCOMEIQUEUE_RCM_ACTION_VALUES = {
  REFUND_PATIENT: 'refund_patient',
  APPLY_CREDIT: 'apply_credit',
  INVOICE_FULL_AMOUNT: 'invoice_full_amount',
  CHARGE_CAP_AND_SEND_INVOICE: 'charge_cap_and_invoice_the_balance',
  SEND_INVOICE_AND_AUTO_CHARGE: 'send_invoice_and_auto_charge'
};

export const INCOMEIQUEUE_RCM_SCENARIOS = [
  {
    field: INCOMEIQUEUE_RCM_SCENARIO_FIELDS.OVER_PAYMENT,
    label: 'If the final portion is less than the GFE'
  },
  {
    field: INCOMEIQUEUE_RCM_SCENARIO_FIELDS.UNDER_PAYMENT,
    label: 'If patient responsibility exceeds cap'
  }
];

export const INCOMEIQUEUE_RCM_ACTION_OPTIONS = {
  [INCOMEIQUEUE_RCM_SCENARIO_FIELDS.OVER_PAYMENT]: [
    { value: INCOMEIQUEUE_RCM_ACTION_VALUES.REFUND_PATIENT, label: 'Refund the patient' },
    {
      value: INCOMEIQUEUE_RCM_ACTION_VALUES.APPLY_CREDIT,
      label: 'Apply credit'
    }
  ],
  [INCOMEIQUEUE_RCM_SCENARIO_FIELDS.UNDER_PAYMENT]: [
    {
      value: INCOMEIQUEUE_RCM_ACTION_VALUES.INVOICE_FULL_AMOUNT,
      label: 'Invoice the full amount'
    },
    {
      value: INCOMEIQUEUE_RCM_ACTION_VALUES.CHARGE_CAP_AND_SEND_INVOICE,
      label: 'Charge up to the cap and invoice the balance'
    },
    {
      value: INCOMEIQUEUE_RCM_ACTION_VALUES.SEND_INVOICE_AND_AUTO_CHARGE,
      label: 'Send invoice, and auto-charge after X days'
    }
  ]
};

export const DEFAULT_CHARGE_AFTER_DAYS = 7;
export const MIN_CHARGE_AFTER_DAYS = 1;

// Action values that predate the current enum. Mirror of the backend migration
// (20260626120000_migrateIncomeIQueueRcmLegacyActions) so a config that has not been
// migrated yet in a given environment still displays — and re-saves — as the current shape,
// instead of resolving to the "Select an action" placeholder while a stale value stays stored.
const LEGACY_MIGRATED_CHARGE_AFTER_DAYS = 7;

const LEGACY_ACTION_MAP = {
  charge_cap_same_invoice: INCOMEIQUEUE_RCM_ACTION_VALUES.CHARGE_CAP_AND_SEND_INVOICE,
  charge_cap_separate_invoice: {
    action: INCOMEIQUEUE_RCM_ACTION_VALUES.SEND_INVOICE_AND_AUTO_CHARGE,
    charge_after: LEGACY_MIGRATED_CHARGE_AFTER_DAYS
  }
};

// Normalize a stored scenario value (null | string | { action, charge_after }) to the current
// enum, translating any legacy action. Returns the input unchanged when there is nothing to map.
export const normalizeScenarioValue = (value) => {
  if (!value) return value;

  const action = typeof value === 'string' ? value : value.action;
  const mapped = LEGACY_ACTION_MAP[action];
  if (mapped === undefined) return value;

  if (typeof mapped === 'string') {
    return typeof value === 'object' ? { ...value, action: mapped } : mapped;
  }

  return {
    ...(typeof value === 'object' ? value : {}),
    action: mapped.action,
    charge_after:
      (typeof value === 'object' ? value.charge_after : undefined) ?? mapped.charge_after
  };
};

// A stored scenario value is either null, a (possibly legacy) action string, or
// { action, charge_after }. These helpers read it back regardless of which shape was persisted.
export const getScenarioAction = (value) => {
  const normalized = normalizeScenarioValue(value);
  if (!normalized) return null;
  return typeof normalized === 'string' ? normalized : (normalized.action ?? null);
};

export const getScenarioChargeAfter = (value) => {
  const normalized = normalizeScenarioValue(value);
  return normalized && typeof normalized === 'object' ? (normalized.charge_after ?? null) : null;
};

const buildDefaultNetworkScenarios = () =>
  INCOMEIQUEUE_RCM_SCENARIOS.reduce((acc, { field }) => {
    acc[field] = null;
    return acc;
  }, {});

export const DEFAULT_INCOMEIQUEUE_RCM = {
  [INCOMEIQUEUE_RCM_NETWORK_TYPES.IN_NETWORK]: buildDefaultNetworkScenarios(),
  [INCOMEIQUEUE_RCM_NETWORK_TYPES.OUT_OF_NETWORK]: buildDefaultNetworkScenarios(),
  [INCOMEIQUEUE_RCM_NETWORK_TYPES.SELF_PAY]: buildDefaultNetworkScenarios()
};

export const hydrateNetworkScenarios = (persisted, networkType) => {
  const stored = persisted?.[networkType] || {};
  return INCOMEIQUEUE_RCM_SCENARIOS.reduce((acc, { field }) => {
    acc[field] = normalizeScenarioValue(stored[field] ?? null);
    return acc;
  }, {});
};
